import { useState, type ReactNode } from "react";
import { FilterGroup, FilterOption, FilterSidebar, StatusDot, TextInput } from "@amber/ui";
import type { FacetValue, ItemStatus, ItemsResponse } from "../../contracts/index";
import { listOf, toggled } from "../filterList";
import type { BacklogSearch } from "../search";
import { STATUS_TONE } from "./StatusCell";

type ListKey = "status" | "readiness" | "person" | "platform" | "stage" | "family" | "type";

export function BacklogSidebar({
  open,
  data,
  search,
  set,
}: {
  open: boolean;
  data: ItemsResponse | undefined;
  search: BacklogSearch;
  set: (patch: Partial<BacklogSearch>) => void;
}) {
  const [find, setFind] = useState("");
  const facets = data?.facets;
  const clearOf = (key: ListKey | "corrected" | "discovered") =>
    search[key]
      ? () => {
          set({ [key]: undefined });
        }
      : null;
  const list = (
    key: ListKey,
    title: string,
    values: readonly FacetValue[] | undefined,
    icon?: (key: string) => ReactNode,
  ) => (
    <FilterGroup title={title} onClear={clearOf(key)}>
      {(values ?? []).map((v) => (
        <FilterOption
          key={v.key}
          type="checkbox"
          name={key}
          label={v.label}
          count={v.count}
          icon={icon?.(v.key)}
          disabledReason={v.disabled_reason}
          checked={listOf(search[key]).includes(v.key)}
          onChange={() => {
            set({ [key]: toggled(search[key], v.key) });
          }}
        />
      ))}
    </FilterGroup>
  );
  const people = (facets?.person ?? []).filter(
    (p) =>
      find.trim() === "" ||
      p.label.toLowerCase().includes(find.trim().toLowerCase()) ||
      listOf(search.person).includes(p.key),
  );
  return (
    <FilterSidebar label="Backlog filters" open={open}>
      {list("status", "Status", facets?.status, (key) =>
        key in STATUS_TONE ? <StatusDot tone={STATUS_TONE[key as ItemStatus]} /> : null,
      )}
      {list("readiness", "Readiness", facets?.readiness)}
      <FilterGroup title="Corrected" onClear={clearOf("corrected")}>
        {(facets?.corrected ?? []).map((c) => (
          <FilterOption
            key={c.key}
            type="checkbox"
            name="corrected"
            label={c.label}
            count={c.count}
            checked={search.corrected === c.key}
            onChange={() => {
              const key = c.key === "yes" || c.key === "no" ? c.key : undefined;
              set({ corrected: search.corrected === key ? undefined : key });
            }}
          />
        ))}
      </FilterGroup>
      <FilterGroup title="Profile" onClear={clearOf("person")}>
        <label className="sr-only" htmlFor="backlog-find-person">
          Find a person
        </label>
        <TextInput
          id="backlog-find-person"
          type="search"
          placeholder="Find a person"
          className="mb-1 h-7"
          value={find}
          onChange={(e) => {
            setFind(e.target.value);
          }}
        />
        {people.map((p) => (
          <FilterOption
            key={p.key}
            type="checkbox"
            name="person"
            label={p.label}
            count={p.count}
            checked={listOf(search.person).includes(p.key)}
            onChange={() => {
              set({ person: toggled(search.person, p.key) });
            }}
          />
        ))}
      </FilterGroup>
      {list("platform", "Platform", facets?.platform)}
      {data && (data.other_accounts > 0 || search.authors === "all") ? (
        <FilterGroup title="X posts" onClear={null}>
          <FilterOption
            type="checkbox"
            name="authors"
            label="Include posts by other accounts"
            count={data.other_accounts}
            checked={search.authors === "all"}
            onChange={() => {
              set({ authors: search.authors === "all" ? undefined : "all" });
            }}
          />
        </FilterGroup>
      ) : null}
      {list("stage", "Current stage", facets?.stage)}
      {list("family", "Source family", facets?.family)}
      {list("type", "Media type", facets?.type)}
      <FilterGroup title="Discovered" onClear={clearOf("discovered")}>
        {(facets?.discovered ?? []).map((d) => (
          <FilterOption
            key={d.key}
            type="checkbox"
            name="discovered"
            label={d.label}
            count={d.count}
            checked={search.discovered === d.key}
            onChange={() => {
              const key =
                d.key === "hour" || d.key === "today" || d.key === "7d" || d.key === "older" ? d.key : undefined;
              set({ discovered: search.discovered === key ? undefined : key });
            }}
          />
        ))}
      </FilterGroup>
    </FilterSidebar>
  );
}
